import BulkEditRequestsSelectorCheckbox from './BulkEditRequestsSelectorCheckbox';
import {
  KoobButton,
  KoobBadge,
  KoobCard,
  KoobInputSelect,
  KoobInputText,
} from '@koob/ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import KoobTitle from '../../../../components/Koob/KoobTitle';
import { getMarkets } from 'api/node/markets';
import { useAsync } from 'react-async';
import { getToConnectionRequests } from 'api/node/toConnectionRequest';
import { isEqual } from 'lodash';
import KoobPlaceholder from 'components/Koob/KoobPlaceholder';
import { useApp } from 'hooks';
import { decodeId } from 'utils';

export default function BulkEditRequestsSelector({
  updatingIds = [],
  setUpdatingIds,
}) {
  const { t } = useTranslation('toConnectionRequest');
  const [{ value: selectedRequests }, , { setValue: setSelectedRequests }] =
    useField('toConnectionRequestIds');
  const { currentOrganizationId, selectedDistributionOrganization } = useApp();
  const dmcOrganizationId =
    selectedDistributionOrganization?.value ?? currentOrganizationId;
  const [search, setSearch] = useState('');

  const [toConnectionRequestFilters, setToConnectionRequestFilters] =
    useState(null);

  const filters = {
    markets: (toConnectionRequestFilters?.markets ?? []).map(({ id }) => id),
    dmcId: decodeId(dmcOrganizationId),
  };

  const { data, isLoading } = useAsync({
    promiseFn: getToConnectionRequests,
    filters,
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });
  const connectionRequests = data?.data || [];

  const { data: marketsData } = useAsync({
    promiseFn: getMarkets,
    filters: {
      order: 'name',
      direction: 'asc',
      page: 0,
      pageSize: 0,
    },
  });

  const markets = (marketsData?.data ?? []).map(({ id, name }) => {
    return {
      id,
      displayName: name,
    };
  });

  const searchResults = connectionRequests.filter((request) => {
    return (
      request?.toOrganization?.name
        ?.toLowerCase()
        ?.includes(search.toLowerCase()) ||
      request?.state?.toLowerCase()?.includes(search.toLowerCase()) ||
      request?.type?.toLowerCase()?.includes(search.toLowerCase())
    );
  });

  useEffect(() => {
    if (!setUpdatingIds || connectionRequests.length === 0) {
      return;
    }
    // Fresh data is authoritative: drop locally tracked ids whose job is done
    setUpdatingIds((current) =>
      current.filter((id) =>
        connectionRequests.some(
          (request) => request.id === id && request.hasActiveJob,
        ),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionRequests]);

  const isUpdating = (request) =>
    !!request?.hasActiveJob || updatingIds.includes(request.id);

  const selectAll = () => {
    if (search?.length > 0) {
      setSelectedRequests([
        ...selectedRequests,
        ...searchResults
          .filter((request) => !isUpdating(request))
          .map((request) => request.id),
      ]);
    } else {
      setSelectedRequests(
        connectionRequests
          .filter((request) => !isUpdating(request))
          .map((request) => request.id),
      );
    }
  };

  const deselectAll = () => {
    if (search?.length > 0) {
      setSelectedRequests(
        selectedRequests.filter(
          (id) => !searchResults.map((request) => request.id).includes(id),
        ),
      );
    } else {
      setSelectedRequests([]);
    }
  };

  const updateFilterValue = useCallback(
    (property, value) => {
      setToConnectionRequestFilters((current) => ({
        ...current,
        [property]: value,
      }));
    },
    [setToConnectionRequestFilters],
  );

  return (
    <KoobCard>
      <div className="flex-col space-y-5">
        <div className="flex justify-between items-center">
          <KoobTitle>{t('bulkEdit.requestSelector.title')}</KoobTitle>
        </div>

        <div className="flex space-x-3 items-center">
          <KoobButton
            variant="primary"
            onClick={() => selectAll()}
            size="sm"
            disabled={selectedRequests.length === connectionRequests.length}
          >
            {t('bulkEdit.requestSelector.selectAll')}
          </KoobButton>

          <KoobButton
            variant="primary"
            onClick={() => deselectAll()}
            size="sm"
            disabled={selectedRequests.length === 0}
          >
            {t('bulkEdit.requestSelector.deselectAll')}
          </KoobButton>

          <KoobBadge color="gray" shape="square">
            {t('bulkEdit.requestSelector.selectedCount', {
              count: selectedRequests.length,
              total: connectionRequests.length ?? 0,
            })}
          </KoobBadge>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <KoobInputText
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('bulkEdit.requestSelector.search')}
          />

          <KoobInputSelect
            placeholder={t('bulkEdit.requestSelector.searchByMarkets')}
            options={markets.map(({ id, displayName }) => ({
              value: id,
              label: displayName,
            }))}
            value={(toConnectionRequestFilters?.markets ?? []).map(
              ({ id }) => id,
            )}
            onValueChange={(ids) => {
              updateFilterValue(
                'markets',
                markets.filter(({ id }) => (ids ?? []).includes(id)),
              );
            }}
            multiple
            searchable
            clearable
          />
        </div>

        <div className="h-56 overflow-scroll bg-gray-50 p-5 rounded-md">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
            {!isLoading ? (
              <>
                {searchResults?.map((request) => (
                  <BulkEditRequestsSelectorCheckbox
                    key={request.id}
                    request={request}
                    selected={selectedRequests.includes(request.id)}
                    setSelected={(selected) => {
                      if (selected) {
                        setSelectedRequests([...selectedRequests, request.id]);
                      } else {
                        setSelectedRequests(
                          selectedRequests.filter((id) => id !== request.id),
                        );
                      }
                    }}
                    updating={isUpdating(request)}
                  />
                ))}
              </>
            ) : (
              <>
                {[...Array(12)].map((_e, index) => (
                  <KoobPlaceholder className="h-10" key={index} />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </KoobCard>
  );
}
